import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SlidePanel } from '@/components/ui/slide-panel';
import { Team, Manager } from '@/types';
import { Loader2 } from 'lucide-react';

interface InternalTeamFormProps {
  initialData?: Team;
  onSubmit: (data: Omit<Team, 'id'>) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  managers: Manager[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InternalTeamForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  managers,
  open,
  onOpenChange
}: InternalTeamFormProps) {
  const [formData, setFormData] = useState<Omit<Team, 'id'>>({
    name: '',
    type: 'INTERNAL_PM',
    manager_id: '',
    biz_dev_id: '',
    manager_name: '',
    biz_dev_rep_name: '',
    empower_created: false,
    ses_created: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize and update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        type: initialData.type || 'INTERNAL_PM',
        manager_id: initialData.manager_id || '',
        biz_dev_id: initialData.biz_dev_id || '',
        manager_name: initialData.manager_name || '',
        biz_dev_rep_name: initialData.biz_dev_rep_name || '',
        empower_created: initialData.empower_created || false,
        ses_created: initialData.ses_created || false
      });
    } else {
      setFormData({
        name: '',
        type: 'INTERNAL_PM',
        manager_id: '',
        biz_dev_id: '',
        manager_name: '',
        biz_dev_rep_name: '',
        empower_created: false,
        ses_created: false
      });
    }
  }, [initialData, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      const { manager_name, biz_dev_rep_name, ...submitData } = formData;
      await onSubmit(submitData);
      setFormData({
        name: '',
        type: 'INTERNAL_PM',
        manager_id: '',
        biz_dev_id: '',
        manager_name: '',
        biz_dev_rep_name: '',
        empower_created: false,
        ses_created: false
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value === 'none' ? '' : value
    }));
  };

  return (
    <SlidePanel
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={initialData ? 'Edit Internal Team' : 'Add Internal Team'}
      width="lg"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
      footer={
        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !formData.name}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {initialData ? 'Updating...' : 'Creating...'}
              </>
            ) : initialData ? (
              'Update Team'
            ) : (
              'Create Team'
            )}
          </Button>
        </div>
      }
    >
      <div className="text-sm text-muted-foreground mb-6">
        {initialData ? 'Update internal team information.' : 'Create a new internal team for your organization.'}
      </div>

      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="flex flex-row items-center gap-0.5">
          <Label htmlFor="name" className="w-32 min-w-[120px] text-right">Team Name *</Label>
          <div className="flex-1">
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter team name"
              disabled={isSubmitting}
              className="w-full"
            />
            {errors.name && (
              <p className="text-sm text-destructive mt-1">{errors.name}</p>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-0.5">
          <Label htmlFor="type" className="w-32 min-w-[120px] text-right">Team Type *</Label>
          <div className="flex-1">
            <SearchableSelect
              id="type"
              value={formData.type}
              onValueChange={(value) => handleSelectChange('type', value)}
              options={[
                { label: 'Internal PM', value: 'INTERNAL_PM' },
                { label: 'Business Development', value: 'BIZ_DEV' }
              ]}
              placeholder="Select team type"
              disabled={isSubmitting}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex flex-row items-center gap-0.5">
          <Label htmlFor="manager_id" className="w-32 min-w-[120px] text-right">Manager</Label>
          <div className="flex-1">
            <SearchableSelect
              id="manager_id"
              value={formData.manager_id || 'none'}
              onValueChange={(value) => handleSelectChange('manager_id', value)}
              options={[
                { label: 'None', value: 'none' },
                ...managers.map(manager => ({
                  label: `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email || 'Unknown Manager',
                  value: manager.id.toString()
                }))
              ]}
              placeholder="Select manager"
              disabled={isSubmitting}
              className="w-full"
            />
          </div>
        </div>
      </form>
    </SlidePanel>
  );
}
